import type { Fetcher } from '../../../src/http';
import { sf311AddressPrefixes } from '../../sf-311/scripts/sf311';
import type {
  FireComplianceSnapshot,
  FireComplianceStatement,
  FireInspection,
  FireInspectionSnapshot,
  FireViolation,
  FireViolationSnapshot,
  SafetyComplianceSnapshot,
  Section604Affidavit,
  Section604Snapshot,
  SourceCoverage,
} from '../../../src/types';

export type SafetySourceName =
  | 'section604'
  | 'fire-inspections'
  | 'fire-violations'
  | 'fire-compliance';

export type SafetySourceRead = <T>(options: {
  source: SafetySourceName;
  scope: Array<string | number | boolean>;
  load: () => Promise<T>;
}) => Promise<T>;

export type SafetyScope = {
  type: 'parcel' | 'condo-building';
  parcelCount: number;
  parcels: Array<{ block: string; lot: string; address: string }>;
  addresses: string[];
  center?: { longitude: number; latitude: number };
};

export function createSafetyComplianceSource(
  fetch: Fetcher,
  options: {
    read?: SafetySourceRead;
    scope?: (
      block: string,
      lot: string,
      addresses: string[],
      isCondo: boolean,
    ) => Promise<SafetyScope>;
  } = {},
) {
  const SECTION_604_API = 'https://data.sf.gov/resource/75h8-2zhw.json';
  const SECTION_604_PAGE =
    'https://data.sf.gov/Housing-and-Buildings/Section-604-Affidavit/75h8-2zhw';
  const FIRE_INSPECTIONS_API = 'https://data.sf.gov/resource/wb4c-6hwj.json';
  const FIRE_INSPECTIONS_PAGE =
    'https://data.sf.gov/Housing-and-Buildings/Fire-Inspections/wb4c-6hwj';
  const FIRE_VIOLATIONS_API = 'https://data.sf.gov/resource/4zuq-2cbe.json';
  const FIRE_VIOLATIONS_PAGE =
    'https://data.sf.gov/Housing-and-Buildings/Fire-Violations/4zuq-2cbe/about_data';
  const ADDRESS_INDEX_API = 'https://data.sf.gov/resource/ramy-di5m.json';
  const SFFD_MAP_API = 'https://sanfrancisco.agencycounter.com/api';
  const SFFD_MAP_PAGE =
    'https://sanfrancisco.agencycounter.com/search/saved/548a63ef-65dc-488b-88ad-30b8e0ed6e38';
  const MAX_ROWS = 10_000;
  const REQUEST_ATTEMPTS = 3;
  const USER_AGENT = 'ParcelWatch/0.1 (+https://parcel-watch.schwartzdev.workers.dev)';
  const CITY_VIEWPORT = {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [-122.60158538818361, 37.60389625445932],
              [-122.27886199951173, 37.60389625445932],
              [-122.27886199951173, 37.830666652929224],
              [-122.60158538818361, 37.830666652929224],
              [-122.60158538818361, 37.60389625445932],
            ],
          ],
        },
        properties: {},
      },
    ],
  };

  type DataSfRow = Record<string, unknown>;

  function clean(value: unknown) {
    return typeof value === 'string'
      ? value
          .replace(/\u00a0/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
      : value === null || value === undefined
        ? ''
        : String(value).trim();
  }

  function scalarFields(row: DataSfRow) {
    return Object.fromEntries(
      Object.entries(row)
        .filter(([, value]) => ['string', 'number', 'boolean'].includes(typeof value))
        .map(([key, value]) => [key, clean(value)]),
    );
  }

  function retryDelay(attempt: number) {
    return new Promise((resolve) => setTimeout(resolve, 150 * 2 ** attempt));
  }

  function dateValue(value: string) {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  function latestDate(rows: DataSfRow[], field: string) {
    return (
      rows.map((row) => clean(row[field])).sort((a, b) => dateValue(b) - dateValue(a))[0] ?? ''
    );
  }

  async function requestJson(url: string | URL, init: RequestInit = {}) {
    let lastError: unknown;
    for (let attempt = 0; attempt < REQUEST_ATTEMPTS; attempt += 1) {
      try {
        const headers = new Headers(init.headers);
        headers.set('Accept', 'application/json');
        headers.set('User-Agent', USER_AGENT);
        const response = await fetch(url, {
          ...init,
          cache: 'no-store',
          headers,
        });
        if (!response.ok)
          throw Object.assign(new Error(`Public safety source returned ${response.status}.`), {
            httpStatus: response.status,
          });
        return (await response.json()) as unknown;
      } catch (error) {
        lastError = error;
        if (attempt < REQUEST_ATTEMPTS - 1) await retryDelay(attempt);
      }
    }
    throw lastError instanceof Error
      ? lastError
      : new Error('Public safety source could not be reached after retries.');
  }

  async function dataSfRows(api: string, where: string) {
    for (let attempt = 0; attempt < REQUEST_ATTEMPTS; attempt += 1) {
      const countUrl = new URL(api);
      countUrl.searchParams.set('$select', 'count(*) as count');
      countUrl.searchParams.set('$where', where);
      const countPayload = await requestJson(countUrl);
      if (!Array.isArray(countPayload))
        throw new Error('DataSF returned an invalid count response.');
      const expectedCount = Number((countPayload[0] as DataSfRow | undefined)?.count);
      if (!Number.isInteger(expectedCount) || expectedCount < 0 || expectedCount > MAX_ROWS) {
        throw new Error('DataSF returned an invalid or unexpectedly large safety record count.');
      }

      const dataUrl = new URL(api);
      dataUrl.searchParams.set('$where', where);
      dataUrl.searchParams.set('$limit', String(MAX_ROWS));
      const payload = await requestJson(dataUrl);
      if (Array.isArray(payload) && payload.length === expectedCount) return payload as DataSfRow[];
      if (attempt < REQUEST_ATTEMPTS - 1) await retryDelay(attempt);
    }
    throw new Error('DataSF returned an incomplete safety record list; baseline was preserved.');
  }

  function parcelPartCandidates(value: string) {
    const upper = value.toUpperCase();
    const unpadded = /^\d+$/.test(upper) ? String(Number(upper)) : upper;
    return [...new Set([upper, unpadded])];
  }

  function sqlValues(values: string[]) {
    return values.map((value) => `'${value.replaceAll("'", "''")}'`).join(',');
  }

  function addressPrefixes(addresses: string[]) {
    return [
      ...new Set(
        sf311AddressPrefixes(addresses).flatMap((address) => {
          const variants = [address];
          const padded = address.replace(
            /^(\d+[A-Z]?(?:-\d+[A-Z]?)?\s+)(\d)(ST|ND|RD|TH)\b/,
            (_, prefix, digit, suffix) => `${prefix}0${digit}${suffix}`,
          );
          const unpadded = address.replace(
            /^(\d+[A-Z]?(?:-\d+[A-Z]?)?\s+)0(\d)(ST|ND|RD|TH)\b/,
            '$1$2$3',
          );
          variants.push(padded, unpadded);
          return variants;
        }),
      ),
    ].sort();
  }

  function normalizedBuildingAddress(value: string) {
    return clean(value)
      .toUpperCase()
      .replace(/,.*$/, '')
      .replace(/\s+#.*$/, '')
      .replace(/\s+(?:UNIT|APT)\s+\S+$/, '')
      .replace(/\bAV\b/g, 'AVE')
      .replace(/[^A-Z0-9 -]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function parseSafetyScope(
    rows: DataSfRow[],
    block: string,
    lot: string,
    addresses: string[],
    isCondo: boolean,
  ): SafetyScope {
    const selectedBases = new Set(addressPrefixes(addresses).map(normalizedBuildingAddress));
    const selectedBlock = block.toUpperCase();
    const selectedLot = lot.toUpperCase();
    const matches = rows.filter((row) => {
      const address = clean(row.address);
      return address && selectedBases.has(normalizedBuildingAddress(address));
    });
    const selectedRow = rows.find(
      (row) =>
        clean(row.block).toUpperCase() === selectedBlock &&
        clean(row.lot).toUpperCase() === selectedLot,
    );
    const scopedRows = isCondo && matches.length ? matches : selectedRow ? [selectedRow] : [];
    const parcels = [
      ...new Map(
        scopedRows.flatMap((row) => {
          const rowBlock = clean(row.block);
          const rowLot = clean(row.lot);
          const address = clean(row.address);
          return rowBlock && rowLot && address
            ? [[`${rowBlock}:${rowLot}`, { block: rowBlock, lot: rowLot, address }] as const]
            : [];
        }),
      ).values(),
    ].sort((a, b) => `${a.block}:${a.lot}`.localeCompare(`${b.block}:${b.lot}`));
    if (
      !parcels.some(
        (parcel) =>
          parcel.block.toUpperCase() === selectedBlock && parcel.lot.toUpperCase() === selectedLot,
      )
    ) {
      parcels.push({ block, lot, address: addresses[0] ?? '' });
    }
    const coordinateRows = scopedRows.flatMap((row) => {
      const rawLongitude = clean(row.longitude);
      const rawLatitude = clean(row.latitude);
      if (!rawLongitude || !rawLatitude) return [];
      const longitude = Number(rawLongitude);
      const latitude = Number(rawLatitude);
      // This service searches San Francisco buildings. Missing values and coordinates
      // outside its city viewport cannot establish a building's compliance scope.
      if (
        !Number.isFinite(longitude) ||
        !Number.isFinite(latitude) ||
        longitude < -122.62 ||
        longitude > -122.28 ||
        latitude < 37.6 ||
        latitude > 37.84
      )
        return [];
      return [{ longitude, latitude }];
    });
    const center = coordinateRows.length
      ? {
          longitude:
            coordinateRows.reduce((sum, point) => sum + point.longitude, 0) / coordinateRows.length,
          latitude:
            coordinateRows.reduce((sum, point) => sum + point.latitude, 0) / coordinateRows.length,
        }
      : undefined;
    const buildingAddresses = isCondo
      ? [...new Set([...addresses, ...parcels.map((parcel) => parcel.address), ...selectedBases])]
      : [...new Set(addresses)];
    return {
      type: isCondo ? 'condo-building' : 'parcel',
      parcelCount: parcels.length,
      parcels,
      addresses: buildingAddresses,
      ...(center ? { center } : {}),
    };
  }

  async function fetchSafetyScope(
    block: string,
    lot: string,
    addresses: string[],
    isCondo: boolean,
  ) {
    const where = `upper(block) in(${sqlValues(parcelPartCandidates(block))})`;
    const rows = await dataSfRows(ADDRESS_INDEX_API, where);
    return parseSafetyScope(rows, block, lot, addresses, isCondo);
  }

  function addressWhere(addresses: string[]) {
    const prefixes = addressPrefixes(addresses);
    if (!prefixes.length)
      throw new Error('No parcel address could be used for the fire safety search.');
    return prefixes
      .map((address) => `upper(address) like '${address.replaceAll("'", "''")}%'`)
      .join(' or ');
  }

  function parseSection604Rows(rows: DataSfRow[]): Section604Affidavit[] {
    return rows
      .map((row, index) => {
        const low = clean(row.street_number_low);
        const high = clean(row.street_number_high);
        const street = [clean(row.street_name), clean(row.street_suffix)].filter(Boolean).join(' ');
        const unit = clean(row.unit);
        const address = [
          `${low}${high && high !== low ? `-${high}` : ''}`,
          street,
          unit ? `#${unit}` : '',
        ]
          .filter(Boolean)
          .join(' ');
        const inspectionDate = clean(row.inspection_date);
        const item = clean(row.item);
        const id =
          item || [clean(row.block), clean(row.lot), inspectionDate, address, index].join(':');
        if (!clean(row.block) || !clean(row.lot) || !id)
          throw new Error('Section 604 returned an incomplete affidavit record.');
        return {
          id,
          address,
          inspectionDate,
          comments: clean(row.comments),
          sourceUrl: SECTION_604_PAGE,
          fields: scalarFields(row),
        };
      })
      .sort((a, b) => dateValue(b.inspectionDate) - dateValue(a.inspectionDate));
  }

  function parseFireInspectionRows(rows: DataSfRow[]): FireInspection[] {
    const inspections = rows.map((row) => {
      const id = clean(row.inspection_number);
      if (!id || !clean(row.address))
        throw new Error('SFFD returned an incomplete inspection record.');
      return {
        id,
        address: clean(row.address),
        type: clean(row.inspection_type),
        description: clean(row.inspection_type_description),
        status: clean(row.inspection_status),
        startedAt: clean(row.inspection_start_date),
        endedAt: clean(row.inspection_end_date),
        sourceUrl: FIRE_INSPECTIONS_PAGE,
        fields: scalarFields(row),
      };
    });
    if (new Set(inspections.map((record) => record.id)).size !== inspections.length) {
      throw new Error('SFFD returned duplicate inspection records.');
    }
    return inspections.sort((a, b) => dateValue(b.startedAt) - dateValue(a.startedAt));
  }

  function parseFireViolationRows(rows: DataSfRow[]): FireViolation[] {
    const violations = rows.map((row) => {
      const id = clean(row.violation_id);
      if (!id || !clean(row.address))
        throw new Error('SFFD returned an incomplete fire violation record.');
      return {
        id,
        inspectionNumber: clean(row.inspection_number),
        number: clean(row.violation_number),
        address: clean(row.address),
        status: clean(row.status),
        violationDate: clean(row.violation_date),
        closeDate: clean(row.close_date),
        description: clean(row.violation_item_description),
        correctiveAction: clean(row.corrective_action),
        sourceUrl: FIRE_VIOLATIONS_PAGE,
        fields: scalarFields(row),
      };
    });
    if (new Set(violations.map((record) => record.id)).size !== violations.length) {
      throw new Error('SFFD returned duplicate fire violation records.');
    }
    return violations.sort((a, b) => dateValue(b.violationDate) - dateValue(a.violationDate));
  }

  function absoluteMapLink(value: string) {
    try {
      const url = new URL(value, 'https://sanfrancisco.agencycounter.com');
      return ['https:', 'http:'].includes(url.protocol) ? url.toString() : '';
    } catch {
      return '';
    }
  }

  function parseFireComplianceDetails(
    payload: unknown,
    fallbackAddress: string,
  ): FireComplianceStatement[] {
    if (!payload || typeof payload !== 'object')
      throw new Error('SFFD property map returned an invalid detail response.');
    const data = (payload as { data?: unknown }).data;
    if (!data || typeof data !== 'object')
      throw new Error('SFFD property map returned an incomplete detail response.');
    const details = (data as { details?: unknown }).details;
    if (!Array.isArray(details)) throw new Error('SFFD property map omitted location details.');

    const statements: FireComplianceStatement[] = [];
    for (const rawRecord of details) {
      if (!rawRecord || typeof rawRecord !== 'object')
        throw new Error('SFFD property map returned an invalid location detail.');
      const record = rawRecord as DataSfRow;
      const rawCompliance = record.compliance;
      const entries =
        clean(record.group).toLowerCase() === 'compliance'
          ? [record]
          : rawCompliance === undefined || rawCompliance === null
            ? []
            : Array.isArray(rawCompliance)
              ? rawCompliance
              : [rawCompliance];
      for (const rawEntry of entries) {
        if (!rawEntry || typeof rawEntry !== 'object')
          throw new Error('SFFD property map returned an incomplete compliance statement.');
        const entry = rawEntry as DataSfRow;
        const date = clean(entry.date);
        const rawLink = clean(entry.link);
        const documentUrl = rawLink ? absoluteMapLink(rawLink) : '';
        const address = clean(record.address) || fallbackAddress;
        if (!date || !documentUrl)
          throw new Error('SFFD property map returned an incomplete compliance statement.');
        statements.push({
          id: documentUrl,
          address,
          date,
          documentUrl,
          sourceUrl: documentUrl,
          fields: { ...scalarFields(entry), Address: address },
        });
      }
    }
    return statements;
  }

  function numericAgencyReference(statement: FireComplianceStatement) {
    const match = statement.fields.agency_reference?.match(/\d+/);
    return match ? Number(match[0]) : 0;
  }

  function dedupeFireComplianceStatements(statements: FireComplianceStatement[]) {
    const deduped = new Map<string, FireComplianceStatement>();
    for (const statement of statements) {
      const occupantNumber = statement.fields.occupant_number ?? '';
      const key = occupantNumber
        ? [normalizedBuildingAddress(statement.address), statement.date, occupantNumber].join(':')
        : statement.id;
      const existing = deduped.get(key);
      if (!existing || numericAgencyReference(statement) > numericAgencyReference(existing)) {
        deduped.set(key, statement);
      }
    }
    return [...deduped.values()];
  }

  async function fetchSection604(parcels: SafetyScope['parcels']): Promise<Section604Snapshot> {
    const where = parcels
      .map(
        (parcel) =>
          `(upper(block) in(${sqlValues(parcelPartCandidates(parcel.block))}) and upper(lot) in(${sqlValues(parcelPartCandidates(parcel.lot))}))`,
      )
      .join(' or ');
    const rows = await dataSfRows(SECTION_604_API, where);
    const fetchedAt = new Date().toISOString();
    const affidavits = parseSection604Rows(rows);
    return {
      sourceUrl: SECTION_604_PAGE,
      resultCount: affidavits.length,
      affidavits,
      updateFrequency: 'Yearly',
      dataAsOf: latestDate(rows, 'inspection_date'),
      fetchedAt,
    };
  }

  async function fetchFireInspections(addresses: string[]): Promise<FireInspectionSnapshot> {
    const rows = await dataSfRows(FIRE_INSPECTIONS_API, addressWhere(addresses));
    const inspections = parseFireInspectionRows(rows);
    return {
      sourceUrl: FIRE_INSPECTIONS_PAGE,
      resultCount: inspections.length,
      inspections,
      updateFrequency: 'Daily',
      dataAsOf: latestDate(rows, 'data_as_of'),
      fetchedAt: new Date().toISOString(),
    };
  }

  async function fetchFireViolations(addresses: string[]): Promise<FireViolationSnapshot> {
    const rows = await dataSfRows(FIRE_VIOLATIONS_API, addressWhere(addresses));
    const violations = parseFireViolationRows(rows);
    return {
      sourceUrl: FIRE_VIOLATIONS_PAGE,
      resultCount: violations.length,
      violations,
      updateFrequency: 'Daily',
      dataAsOf: latestDate(rows, 'data_as_of'),
      fetchedAt: new Date().toISOString(),
    };
  }

  function agencyCriteria(address: string, locationGuids?: string[]) {
    return {
      type: [1165],
      status_category_id_internal: [1, 2],
      ___record_public___override: ['default'],
      ___address: address,
      ___viewport: CITY_VIEWPORT,
      ...(locationGuids ? { ___location: locationGuids } : {}),
    };
  }

  function agencyBuildingCriteria(
    center: NonNullable<SafetyScope['center']>,
    locationGuids?: string[],
  ) {
    const delta = 0.00035;
    const west = center.longitude - delta;
    const east = center.longitude + delta;
    const south = center.latitude - delta;
    const north = center.latitude + delta;
    return {
      ___record_public___override: ['default'],
      ___viewport: {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [
                [
                  [west, south],
                  [east, south],
                  [east, north],
                  [west, north],
                  [west, south],
                ],
              ],
            },
            properties: {},
          },
        ],
      },
      ...(locationGuids ? { ___location: locationGuids } : {}),
    };
  }

  async function agencyPost(path: string, body: unknown) {
    return requestJson(`${SFFD_MAP_API}${path}`, {
      method: 'POST',
      headers: {
        'Agency-Counter-Tenant': 'sanfrancisco',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
  }

  async function statementsForAddress(address: string) {
    // The parcel's verified address already defines the search scope. Address
    // suggestions previously accepted only an exact spelling match, so they
    // added an availability dependency without providing another identity.
    const canonical = addressPrefixes([address])[0] ?? address;
    const locationPayload = await agencyPost('/search/location', agencyCriteria(canonical));
    const locationData = (locationPayload as { data?: unknown }).data;
    const features =
      locationData && typeof locationData === 'object'
        ? (locationData as { features?: unknown }).features
        : undefined;
    if (!Array.isArray(features))
      throw new Error('SFFD property map returned an incomplete location response.');
    const locationGuids = features
      .map((feature) => {
        if (!feature || typeof feature !== 'object') return '';
        const properties = (feature as { properties?: unknown }).properties;
        return properties && typeof properties === 'object'
          ? clean((properties as DataSfRow).guid)
          : '';
      })
      .filter(Boolean);
    if (locationGuids.length !== features.length)
      throw new Error('SFFD property map returned incomplete location identifiers.');
    if (!locationGuids.length) return [];
    const detailPayload = await agencyPost(
      '/search/detail',
      agencyCriteria(canonical, locationGuids),
    );
    return parseFireComplianceDetails(detailPayload, canonical);
  }

  async function statementsForCondoBuilding(scope: SafetyScope) {
    if (!scope.center)
      throw new Error(
        'SFFD building geometry is unavailable; fire statement coverage is incomplete.',
      );
    const locationPayload = await agencyPost(
      '/search/location',
      agencyBuildingCriteria(scope.center),
    );
    const locationData = (locationPayload as { data?: unknown }).data;
    const features =
      locationData && typeof locationData === 'object'
        ? (locationData as { features?: unknown }).features
        : undefined;
    if (!Array.isArray(features))
      throw new Error('SFFD property map returned an incomplete building location response.');
    const locationGuids = features
      .map((feature) => {
        if (!feature || typeof feature !== 'object') return '';
        const properties = (feature as { properties?: unknown }).properties;
        return properties && typeof properties === 'object'
          ? clean((properties as DataSfRow).guid)
          : '';
      })
      .filter(Boolean);
    if (locationGuids.length !== features.length)
      throw new Error('SFFD property map returned incomplete location identifiers.');
    if (!locationGuids.length) return [];
    const buildingAddresses = new Set(scope.addresses.map(normalizedBuildingAddress));
    const detailCriteria = agencyBuildingCriteria(scope.center, locationGuids);
    const readPass = async () => {
      const detailPayload = await agencyPost('/search/detail', detailCriteria);
      return parseFireComplianceDetails(detailPayload, scope.addresses[0] ?? '').filter(
        (statement) => buildingAddresses.has(normalizedBuildingAddress(statement.address)),
      );
    };
    const [first, second] = await Promise.all([readPass(), readPass()]);
    const sameRecords = (left: FireComplianceStatement[], right: FireComplianceStatement[]) =>
      left.length === right.length &&
      left.every((statement) => right.some((candidate) => candidate.id === statement.id));
    if (sameRecords(first, second)) return first;
    const third = await readPass();
    if (sameRecords(first, third)) return first;
    if (sameRecords(second, third)) return second;
    throw new Error(
      'SFFD property map returned inconsistent building records; baseline was preserved.',
    );
  }

  async function fetchFireCompliance(scope: SafetyScope): Promise<FireComplianceSnapshot> {
    const addressResults =
      scope.type === 'condo-building'
        ? [await statementsForCondoBuilding(scope)]
        : await Promise.all([...new Set(scope.addresses)].map(statementsForAddress));
    const statements = dedupeFireComplianceStatements([
      ...new Map(addressResults.flat().map((statement) => [statement.id, statement])).values(),
    ]).sort((a, b) => dateValue(b.date) - dateValue(a.date));
    return {
      sourceUrl: SFFD_MAP_PAGE,
      resultCount: statements.length,
      statements,
      updateFrequency: 'Not published',
      dataAsOf: statements[0]?.date ?? '',
      fetchedAt: new Date().toISOString(),
    };
  }

  async function fetchSafetyComplianceUncached(
    block: string,
    lot: string,
    addresses: string[],
    isCondo: boolean,
  ) {
    const scope = await (options.scope ?? fetchSafetyScope)(block, lot, addresses, isCondo);
    const scopeKey = [
      scope.type,
      ...scope.addresses.slice().sort(),
      ...scope.parcels.map((parcel) => `${parcel.block}:${parcel.lot}`).sort(),
    ];
    const read: SafetySourceRead = options.read ?? (async ({ load }) => load());
    const results = await Promise.allSettled([
      read({
        source: 'section604',
        scope: scopeKey,
        load: () => fetchSection604(scope.parcels),
      }),
      read({
        source: 'fire-inspections',
        scope: scopeKey,
        load: () => fetchFireInspections(scope.addresses),
      }),
      read({
        source: 'fire-violations',
        scope: scopeKey,
        load: () => fetchFireViolations(scope.addresses),
      }),
      read({
        source: 'fire-compliance',
        scope: scopeKey,
        load: () => fetchFireCompliance(scope),
      }),
    ]);
    const checkedAt = new Date().toISOString();
    const sources = [
      ['section604', SECTION_604_PAGE],
      ['fire-inspections', FIRE_INSPECTIONS_PAGE],
      ['fire-violations', FIRE_VIOLATIONS_PAGE],
      ['fire-compliance', SFFD_MAP_PAGE],
    ] as const;
    const coverage: SourceCoverage[] = results.map((result, index) => ({
      source: sources[index]![0],
      sourceUrl: sources[index]![1],
      checkedAt,
      status: result.status === 'fulfilled' ? 'complete' : 'failed',
      ...(result.status === 'rejected'
        ? {
            error: result.reason instanceof Error ? result.reason.message : 'Source unavailable',
          }
        : {}),
    }));
    const [section604, fireInspections, fireViolations, statements] = results;
    const empty = { resultCount: 0, dataAsOf: '', fetchedAt: checkedAt };
    const snapshot: SafetyComplianceSnapshot = {
      scope: {
        type: scope.type,
        parcelCount: scope.parcelCount,
        parcels: scope.parcels,
      },
      section604:
        section604.status === 'fulfilled'
          ? section604.value
          : {
              ...empty,
              sourceUrl: SECTION_604_PAGE,
              updateFrequency: 'Yearly',
              affidavits: [],
            },
      fireInspections:
        fireInspections.status === 'fulfilled'
          ? fireInspections.value
          : {
              ...empty,
              sourceUrl: FIRE_INSPECTIONS_PAGE,
              updateFrequency: 'Daily',
              inspections: [],
            },
      fireViolations:
        fireViolations.status === 'fulfilled'
          ? fireViolations.value
          : {
              ...empty,
              sourceUrl: FIRE_VIOLATIONS_PAGE,
              updateFrequency: 'Daily',
              violations: [],
            },
      statements:
        statements.status === 'fulfilled'
          ? statements.value
          : {
              ...empty,
              sourceUrl: SFFD_MAP_PAGE,
              updateFrequency: 'Not published',
              statements: [],
            },
    };
    return { snapshot, coverage };
  }

  function fetchSafetyComplianceSnapshot(
    block: string,
    lot: string,
    addresses: string[],
    isCondo = false,
  ) {
    return fetchSafetyComplianceUncached(block, lot, addresses, isCondo);
  }

  async function fetchSource(
    source: SafetySourceName,
    block: string,
    lot: string,
    addresses: string[],
    isCondo = false,
  ) {
    const scope = await (options.scope ?? fetchSafetyScope)(block, lot, addresses, isCondo);
    const data =
      source === 'section604'
        ? await fetchSection604(scope.parcels)
        : source === 'fire-inspections'
          ? await fetchFireInspections(scope.addresses)
          : source === 'fire-violations'
            ? await fetchFireViolations(scope.addresses)
            : await fetchFireCompliance(scope);
    return {
      scope: { type: scope.type, parcelCount: scope.parcelCount, parcels: scope.parcels },
      data,
    };
  }

  return {
    fetchSource,
    fetchSafetyScope,
    parseSafetyScope,
    parseSection604Rows,
    parseFireInspectionRows,
    parseFireViolationRows,
    parseFireComplianceDetails,
    dedupeFireComplianceStatements,
    fetchSafetyComplianceSnapshot,
  };
}

export const {
  parseSafetyScope,
  parseSection604Rows,
  parseFireInspectionRows,
  parseFireViolationRows,
  parseFireComplianceDetails,
  dedupeFireComplianceStatements,
} = createSafetyComplianceSource((...args) => globalThis.fetch(...args));
